import { useContext, useRef } from "react";

import { useTranslation } from "react-i18next";
import { CSSTransition } from "react-transition-group";

import { css } from "@emotion/react";
import CircularProgress from "@mui/material/CircularProgress";

import {
    colors,
    DeleteOutlineIcon,
    DocOutlineIcon
} from "@dotnet/telematics-design-system";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import {
    TOOLTIP_PLACEMENT,
    TooltipGeneral
} from "components/DesignComponents/Tooltips";
import DropzoneContext from "context/dropzone/dropzoneContext";
import { useFileSize } from "shared/hooks/useFileSize";
import { body4Regular } from "shared/styles/typography";
import { FILE_UPLOAD_STATUS } from "shared/types/enums";

import { UploadedFileMultipleProps } from "../types";
import StatusIcon from "./StatusIcon";

const UploadedFileMultiple = ({
    name,
    id,
    size,
    isLoading,
    fileUploadStatus,
    failedUpload,
    in: inProp,
    onExited,
    onEnter,
    onEntering,
    onEntered,
    onExit,
    onExiting,
    ...props
}: UploadedFileMultipleProps) => {
    const { gray200, gray700, textPlaceholder } = colors;

    const { removeFile } = useContext(DropzoneContext);

    const { t } = useTranslation();
    const { renderFileSize } = useFileSize();

    const nodeRef = useRef(null);

    return (
        <CSSTransition
            in={inProp}
            onExited={onExited}
            onEnter={onEnter}
            onEntering={onEntering}
            onEntered={onEntered}
            onExit={onExit}
            onExiting={onExiting}
            nodeRef={nodeRef}
            timeout={300}
            classNames="uploaded-file-multiple"
        >
            <div
                {...props}
                ref={nodeRef}
                css={css({
                    padding: "7px 15px",
                    border: `1px solid ${gray200}`,
                    borderRadius: "8px",

                    "& > div": {
                        display: "flex",
                        alignItems: "center"
                    },
                    "&.uploaded-file-multiple-enter": {
                        opacity: "0"
                    },

                    "&.uploaded-file-multiple-enter-active": {
                        opacity: "1",
                        transition: "opacity 300ms ease-in"
                    },

                    "&.uploaded-file-multiple-exit": {
                        opacity: "1"
                    },

                    "&.uploaded-file-multiple-exit-active": {
                        opacity: "0",
                        transition: "opacity 150ms ease-in"
                    }
                })}
            >
                <div
                    css={css({
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",

                        "& > div": {
                            display: "flex",
                            alignItems: "center"
                        }
                    })}
                >
                    <div>
                        <DocOutlineIcon
                            css={css({
                                alignSelf: "flex-start",
                                marginRight: "8px",
                                color: textPlaceholder
                            })}
                        />

                        <div
                            css={css({
                                flex: "1",
                                display: "flex",
                                justifyContent: "center",
                                flexDirection: "column"
                            })}
                        >
                            <span
                                css={css({
                                    color: gray700,
                                    fontSize: "14px",
                                    lineHeight: "20px",
                                    fontWeight: "500",
                                    letterSpacing: "0.1px",
                                    wordBreak: "break-word"
                                })}
                            >
                                {name}
                            </span>

                            {fileUploadStatus ===
                                FILE_UPLOAD_STATUS.Success && (
                                <span
                                    css={[
                                        body4Regular,
                                        { color: textPlaceholder }
                                    ]}
                                >
                                    {renderFileSize(size)}
                                </span>
                            )}
                        </div>
                    </div>

                    <div
                        css={css({
                            alignSelf: "flex-start",
                            marginLeft: "8px"
                        })}
                    >
                        {isLoading && (
                            <div
                                css={css({
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    width: "36px",
                                    height: "36px",
                                    marginRight: "4px"
                                })}
                            >
                                <CircularProgress size={20} />
                            </div>
                        )}

                        {fileUploadStatus === FILE_UPLOAD_STATUS.Ready ? (
                            <TooltipGeneral
                                placement={TOOLTIP_PLACEMENT.Top}
                                title={t("Button##delete")}
                                disableTouchListener
                                disableHoverListener={isLoading}
                            >
                                <Button
                                    color={BUTTON_COLORS.PrimarySubtle}
                                    size={BUTTON_SIZES.Small}
                                    variant={BUTTON_VARIANTS.IconOnly}
                                    icon={<DeleteOutlineIcon />}
                                    disabled={isLoading}
                                    onClick={() => removeFile(id)}
                                />
                            </TooltipGeneral>
                        ) : (
                            <StatusIcon uploadStatus={fileUploadStatus!} />
                        )}
                    </div>
                </div>

                {failedUpload && (
                    <div
                        css={css({
                            fontSize: "14px",
                            fontWeight: 400,
                            lineHeight: "20px",
                            letterSpacing: "0.1px",
                            wordBreak: "break-word"
                        })}
                    >
                        {t(`Flash##${failedUpload}`, {
                            defaultValue: failedUpload
                        })}
                    </div>
                )}
            </div>
        </CSSTransition>
    );
};

export default UploadedFileMultiple;
